import type { Address } from '@einwert/valuation-api-client'
import {
    AddressInput,
    coordinatesToGoogleLatLng,
    FormTemplate,
    FormTextField,
    GoogleSearchBar,
    InfoTooltip,
    Map,
    Marker,
    PageGridItem,
    PlaceDetails,
} from 'ComponentLibrary'
import { getMapsApiKey } from 'ComponentLibrary/src/common/environment'
import { type StepProps } from '../types'
import { FormProvider, useForm } from 'react-hook-form'
import { GridLegacy as Grid, Stack, styled } from '@mui/material'
import { useCallback, useState } from 'react'
import type { Coordinates as GeoCoordinates } from '@einwert/valuation-api-client'
import { FunnelFooter } from 'ClientPortal/src/pages/Orders/CreateOrder/FunnelFooter/FunnelFooter'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTranslation } from 'react-i18next'

type AddressWithReference = Address & { customer_asset_reference?: string }

export function NewAssetSubStep({ order, activeStep, handleNext, handleBack, dispatch, isLastStep = false }: StepProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage.asset' })
    const locale = useCurrentResolvedLocale()
    const [coordinates, setCoordinates] = useState<GeoCoordinates | undefined>(order.address?.coordinates)
    const form = useForm<AddressWithReference>({
        defaultValues: {
            street: order?.address?.street,
            house_number: order?.address?.house_number,
            state: order?.address?.state,
            city: order?.address?.city,
            zip: order?.address?.zip,
            country: order?.address?.country || t('address.defaultCountry'),
            customer_asset_reference: order?.customer_asset_reference,
        },
    })

    const dispatchAddressWithReference = ({ customer_asset_reference, ...address }: AddressWithReference) => {
        dispatch({
            type: 'updateAssetAddress',
            payload: { ...address, coordinates },
        })
        dispatch({
            type: 'updateCustomerAssetReference',
            payload: customer_asset_reference,
        })
    }

    const onSubmitAndNavigate = form.handleSubmit((data: AddressWithReference) => {
        dispatchAddressWithReference(data)
        handleNext()
    })

    const onHandleBack = () => {
        const [street, house_number, state, city, zip, country, customer_asset_reference] = form.getValues([
            'street',
            'house_number',
            'state',
            'city',
            'zip',
            'country',
            'customer_asset_reference',
        ])
        dispatchAddressWithReference({
            street,
            house_number,
            state,
            city,
            zip,
            country,
            customer_asset_reference,
        })
        handleBack()
    }

    const onSetPlaceDetails = useCallback(
        (placeDetails: PlaceDetails | null) => {
            if (placeDetails) {
                form.setValue('street', placeDetails.street)
                form.setValue('house_number', placeDetails.streetNumber)
                form.setValue('state', placeDetails.state)
                form.setValue('city', placeDetails.city)
                form.setValue('zip', placeDetails.zipcode)
                form.setValue('country', placeDetails.country)
                setCoordinates(placeDetails.coords)
                form.clearErrors()
            }
        },
        [form]
    )

    return (
        <>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <Stack direction="column">
                    <FormTemplate>
                        <Grid item xs={12}>
                            <GoogleSearchBar
                                apiKey={getMapsApiKey()}
                                language={locale}
                                setPlaceDetailsCallback={onSetPlaceDetails}
                                copy={{
                                    noMatchingAddress: t('addressSearch.noMatchingAddress'),
                                    enterAddressToShowOptions: t('addressSearch.enterAddressToShowOptions'),
                                    consentRequiredLabel: t('addressSearch.consentRequiredLabel'),
                                    searchLabel: t('addressSearch.searchLabel'),
                                }}
                            />
                        </Grid>
                        <FormProvider {...form}>
                            <AddressInput
                                labels={{
                                    street: t('address.street'),
                                    house_number: t('address.houseNumber'),
                                    state: t('address.state'),
                                    city: t('address.city'),
                                    zip: t('address.zip'),
                                    country: t('address.country'),
                                }}
                            />
                            <FormTextField
                                errors={form.formState.errors}
                                label={t('customerAssetReference')}
                                control={form.control}
                                fieldName="customer_asset_reference"
                                fullWidth
                                endAdornment={<InfoTooltip text={t('customerAssetReferenceTooltip')} />}
                            />
                        </FormProvider>
                    </FormTemplate>
                </Stack>
            </PageGridItem>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <MapContainer>
                    <Map
                        center={coordinatesToGoogleLatLng(coordinates || CENTRAL_GERMANY)}
                        apiKey={getMapsApiKey()}
                        language={locale}
                        zoom={coordinates ? 15 : 5}
                        options={{
                            mapTypeControl: false,
                        }}
                    >
                        {coordinates && <Marker lat={coordinates.latitude} lng={coordinates.longitude} />}
                    </Map>
                </MapContainer>
            </PageGridItem>
            <FunnelFooter activeStep={activeStep} isLastStep={isLastStep} handleNext={onSubmitAndNavigate} handleBack={onHandleBack} />
        </>
    )
}

const MapContainer = styled('div')(() => ({
    height: '376px',
}))

const CENTRAL_GERMANY: GeoCoordinates = {
    latitude: 51.1657,
    longitude: 10.4515,
}
